/* =============================================================================
   CORE / PAGINATED-LIST — Cross-Cutting-Helper für Listen-Pagination
   =============================================================================
   Session 59 / Block H
   ----------------------------------------------------------------------------
   Jonas-UX-Befund S59: Listen im ganzen Tool (Archiv, Dashboard, Restore-
   Sektionen) zeigen alle Einträge auf einmal — bei 100+ Items unübersichtlich.

   Pattern: Default 5–10 sichtbar, danach „Mehr anzeigen"-Button (+10 / +25 /
   alle). State pro Liste in einer Map gehalten, damit Re-Renders nicht den
   Expand-Zustand zurücksetzen.

   Memory-Konformität:
   - `feedback_gg_counter_single_source`: zentrale Logik, keine Duplikation.
   - `feedback_gg_non_destructive_render`: State überlebt Re-Renders.

   Usage:
     renderPaginatedList({
       items: array,
       containerId: 'foo-list',         // DOM-id für State-Key
       initial: 5,                      // default
       step: 10,                        // pro „Mehr"-Klick
       renderItem: (item, i) => string, // pro-item HTML
       wrapStyle: 'display:flex; flex-direction:column; gap:8px;'
     })
============================================================================ */

// Map<containerId, number> — wie viele Items aktuell pro Liste sichtbar
const _visibleCount = new Map();

const DEFAULT_INITIAL = 5;
const DEFAULT_STEP    = 10;

/**
 * Liest den aktuell sichtbaren Count für eine Liste (oder Default).
 */
export function getVisibleCount(containerId, initial = DEFAULT_INITIAL) {
  return _visibleCount.get(containerId) ?? initial;
}

/**
 * Setzt sichtbaren Count (z.B. nach „Mehr anzeigen"-Klick).
 */
export function setVisibleCount(containerId, n) {
  _visibleCount.set(containerId, Math.max(1, Number(n) || DEFAULT_INITIAL));
}

/**
 * Reset für eine Liste (z.B. nach Filter-Change, damit neu von oben gezeigt wird).
 */
export function resetVisibleCount(containerId) {
  _visibleCount.delete(containerId);
}

/**
 * Erweitert die sichtbare Anzahl um `step` (oder bis zum Listen-Ende).
 * Wird vom „Mehr anzeigen"-Button via inline-onclick aufgerufen.
 */
export function expandList(containerId, step = DEFAULT_STEP, totalCount = null) {
  const current = getVisibleCount(containerId);
  let next = current + (Number(step) || DEFAULT_STEP);
  if (totalCount != null && next > totalCount) next = totalCount;
  setVisibleCount(containerId, next);
  // Container nochmal rendern lassen — Caller registriert ein onReRender-Callback
  // ODER ruft selbst die zugehörige render-Funktion auf. Wir feuern hier ein
  // CustomEvent, das interessierte Komponenten abgreifen können.
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('paginated-list:expanded', {
      detail: { containerId, visibleCount: next, totalCount }
    }));
  }
  // Direktes DOM-Update als Fallback: Container neu rendern wenn Renderer
  // im DOM hinterlegt ist (data-paginated-renderer-fn).
  const el = document.getElementById(containerId);
  if (el) {
    const fnName = el.dataset.paginatedRendererFn;
    if (fnName && typeof window[fnName] === 'function') {
      try { window[fnName](); } catch (e) { console.warn('[paginated-list] re-render failed:', e); }
    }
  }
}

/**
 * Direkter HTML-Renderer für pagination-fähige Listen.
 *
 * @param {object} opts
 * @param {Array}  opts.items         — alle Items (volle Liste)
 * @param {string} opts.containerId   — DOM-id, für State-Key + Auto-Re-Render
 * @param {number} [opts.initial=5]   — wie viele initial sichtbar
 * @param {number} [opts.step=10]     — wie viele pro „Mehr"-Klick
 * @param {Function} opts.renderItem  — (item, idx) => string (HTML)
 * @param {string} [opts.wrapStyle]   — style für den Items-Wrapper
 * @param {string} [opts.emptyHtml]   — HTML wenn items.length === 0
 * @param {string} [opts.rendererFn]  — globaler Funktionsname für Auto-Re-Render nach expand
 * @returns {string} kompletter HTML-Block inkl. „Mehr anzeigen"-Button
 */
export function renderPaginatedList(opts) {
  const {
    items,
    containerId,
    initial = DEFAULT_INITIAL,
    step = DEFAULT_STEP,
    renderItem,
    wrapStyle = '',
    emptyHtml = '<div class="hint" style="padding:12px; text-align:center;">Keine Einträge.</div>',
    rendererFn = ''
  } = opts || {};

  if (!Array.isArray(items)) return emptyHtml;
  if (items.length === 0) return emptyHtml;

  const total   = items.length;
  const visible = Math.min(getVisibleCount(containerId, initial), total);
  const shown   = items.slice(0, visible);
  const rest    = total - visible;

  const itemsHtml = shown.map((it, i) => renderItem(it, i)).join('');

  const wrapStyleAttr = wrapStyle ? ` style="${wrapStyle}"` : '';
  const dataRenderer  = rendererFn ? ` data-paginated-renderer-fn="${rendererFn}"` : '';

  // S258-HF — die Handler standen hier als `window.ExpandList && window.ExpandList(…)`.
  // Der Parser in dom-bindings.js kennt nur `bezeichner(args)`; der &&-Kurzschluss
  // fällt auf `{kind:'unknown'}`, der Listener wird registriert und warnt nur — und
  // weil das Attribut danach aus dem DOM entfernt ist, sieht der Knopf funktionsfähig
  // aus. Sieben Stellen in dieser Datei, alle stumm. Der Guard war ohnehin unnötig:
  // dom-bindings prüft vor dem Aufruf selbst, ob die Funktion existiert.
  const moreButton = rest > 0
    ? `<div style="margin-top:12px; text-align:center;">
         <button class="btn btn-secondary btn-sm"
                 onclick="ExpandList('${containerId}', ${step}, ${total})">
           Mehr anzeigen
           <span style="opacity:.7; font-weight:400; margin-left:6px;">
             (${visible} von ${total})
           </span>
         </button>
         ${rest > step ? `
           <button class="btn btn-ghost btn-sm" style="margin-left:8px;"
                   onclick="ExpandList('${containerId}', ${total}, ${total})">
             alle ${total} zeigen
           </button>` : ''}
       </div>`
    : (total > initial
        ? `<div style="margin-top:12px; text-align:center; font-size:.85rem; color:var(--ink-soft);">
             Alle ${total} angezeigt ·
             <button class="btn btn-ghost btn-sm" style="padding:2px 8px;"
                     onclick="ExpandList('${containerId}', ${initial}, ${total}); PaginatedListReset('${containerId}')">
               weniger zeigen
             </button>
           </div>`
        : '');

  return `<div${wrapStyleAttr}${dataRenderer}>${itemsHtml}</div>${moreButton}`;
}

/**
 * Lower-level Helper für Tabellen + andere Render-Layouts wo der Slice
 * separat platziert werden muss (z.B. innerhalb `<tbody>` während der
 * „Mehr"-Button unter dem `<table>` steht).
 *
 * Returns { slice, moreButtonHtml, visible, total, rest }
 */
export function paginate(opts) {
  const {
    items,
    containerId,
    initial = DEFAULT_INITIAL,
    step = DEFAULT_STEP,
    rendererFn = ''
  } = opts || {};

  if (!Array.isArray(items)) {
    return { slice: [], moreButtonHtml: '', visible: 0, total: 0, rest: 0 };
  }

  const total   = items.length;
  const visible = Math.min(getVisibleCount(containerId, initial), total);
  const slice   = items.slice(0, visible);
  const rest    = total - visible;

  // Re-Render-Hook über data-attribut + globalen renderer-Namen
  if (rendererFn && typeof window !== 'undefined') {
    // Container sollte data-paginated-renderer-fn="<name>" tragen, damit
    // expandList den richtigen Re-Renderer trifft.
    // Caller setzt das selbst aufs Wrapper-Element.
  }

  let moreButtonHtml = '';
  if (rest > 0) {
    moreButtonHtml = `
      <div data-paginated-renderer-fn="${rendererFn}" id="${containerId}"
           style="margin-top:12px; text-align:center;">
        <button class="btn btn-secondary btn-sm"
                onclick="ExpandList('${containerId}', ${step}, ${total})">
          Mehr anzeigen
          <span style="opacity:.7; font-weight:400; margin-left:6px;">(${visible} von ${total})</span>
        </button>
        ${rest > step ? `
          <button class="btn btn-ghost btn-sm" style="margin-left:8px;"
                  onclick="ExpandList('${containerId}', ${total}, ${total})">
            alle ${total} zeigen
          </button>` : ''}
      </div>`;
  } else if (total > initial) {
    moreButtonHtml = `
      <div data-paginated-renderer-fn="${rendererFn}" id="${containerId}"
           style="margin-top:12px; text-align:center; font-size:.85rem; color:var(--ink-soft);">
        Alle ${total} angezeigt ·
        <button class="btn btn-ghost btn-sm" style="padding:2px 8px;"
                onclick="PaginatedListReset('${containerId}')">
          weniger zeigen
        </button>
      </div>`;
  }

  return { slice, moreButtonHtml, visible, total, rest };
}

// ---------------------------------------------------------------------------
// Window-Export für inline-onclick
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.PaginatedList = {
    DEFAULT_INITIAL,
    DEFAULT_STEP,
    getVisibleCount,
    setVisibleCount,
    resetVisibleCount,
    expandList,
    paginate,
    renderPaginatedList
  };
  window.ExpandList = expandList;
  window.PaginatedListReset = (id) => { resetVisibleCount(id); expandList(id, 0); };
}
